import { type IInstructionFeedback } from "@/interfaces/api/instruction-feedback.interface"
import { InstructionFeedback } from "@/models/instruction-feedback.model"
import { InstructionFeedbackRepository } from "@/repositories/instruction-feedback-repository"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { Instruction } from "@/models/instruction.model"
import { useAuthStore } from "@/store/auth.store"
import { storeToRefs } from "pinia"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface"
import { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"

export class InstructionFeedbackController extends GenericModelController<
  InstructionFeedback,
  IInstructionFeedback,
  InstructionFeedbackRepository
> {
  private authStore = useAuthStore()

  constructor() {
    const storeFunctionsConfig = { managed: false }

    super(InstructionFeedback, storeFunctionsConfig, new InstructionFeedbackRepository())
  }

  // TODO consider using flat endpoint hierarchy (i.e. planning/feedback) instead of nested
  //  (i.e. planning/instructions/:id/feedback) because there is a lot of arguably unnecessary custom logic here.
  //  Check out InstructionFeedbackActivityController for an example.
  public async createForInstruction(
    instance: InstructionFeedback,
    forInstruction: Instruction,
    serviceProvider?: KnownBusinessEntityContact | null,
    requestConfig?: IRepositoryRequestConfig
  ) {
    if (!serviceProvider) {
      // By default, use the active user's knownBusinessEntityContact as the service provider.
      const { activeUser } = storeToRefs(this.authStore)
      serviceProvider = activeUser.value!.knownBusinessEntityContact!
    }

    instance.serviceProvider = serviceProvider

    const response = await new InstructionFeedbackRepository().saveToInstruction(
      forInstruction,
      instance,
      requestConfig
    )
    if (response.data) {
      instance.isSaved = true
      instance.id = response.data.id
      await this.storeFunctions.createModel(instance)
    }
    return instance
  }

  public async confirmChanges(
    instance: InstructionFeedback,
    requestConfig?: IRepositoryRequestConfig
  ) {
    const result = await new InstructionFeedbackRepository().confirmChanges(instance, requestConfig)
    if (result.status === 200) {
      const updatedInstance = this.storeFunctions.updateModel(result.data)
      instance.confirmedAt = updatedInstance.confirmedAt
    }
    return result.status === 200
  }

  public async deleteFromInstruction(
    instance: Instruction,
    requestConfig?: IRepositoryRequestConfig
  ) {
    if (!instance.feedback) {
      throw new Error("Instruction does not have feedback to delete.")
    }
    const isDeleted = await super.delete(instance.feedback, requestConfig)
    if (isDeleted) {
      delete instance.feedback
    }
    return isDeleted
  }
}
